import React, { useEffect, useState } from 'react';
import {
  Grid,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  TextField,
  CircularProgress,
  Snackbar,
} from '@material-ui/core';
import { SortableTable } from 'common/components/SortableTable';
import { getCandidates } from '../Candidates/actions/getCandidates';
import { generatePdp } from './actions/generatePdp';
import { generateAnswerLog } from './actions/generateAnswerLog';

// Admin-only: generate a candidate's PDP report as a PDF. Self-assessment targets (POC).
// Optionally anonymise the report (admin enters the label, default "Candidate A") for sharing
// outside Competas. A Framework Builder position picker can be added here later.
const COMPLETED = ['Tech Assessed', 'Passport Ready', 'Passport Issued'];

const TABLE_HEADERS = [
  { id: 'crn', align: 'left', numeric: false, label: 'CRN' },
  { id: 'name', align: 'left', numeric: false, label: 'NAME' },
  { id: 'discipline', align: 'left', numeric: false, label: 'DISCIPLINE' },
  { id: 'role', align: 'left', numeric: false, label: 'ROLE' },
  { id: 'status', align: 'left', numeric: false, label: 'STATUS' },
  { id: 'generate', align: 'right', numeric: false, label: '' },
];

function createData(row, onGenerate) {
  const ready = COMPLETED.includes(row.status);
  return [
    { id: 'crn', value: row.crn },
    { id: 'name', value: row.name },
    { id: 'discipline', value: row.discipline },
    { id: 'role', value: row.role },
    { id: 'status', value: row.status },
    {
      id: 'generate',
      align: 'right',
      value: (
        <Button
          size="small"
          variant="outlined"
          color="primary"
          disabled={!ready}
          onClick={() => onGenerate(row)}
        >
          Generate PDP
        </Button>
      ),
    },
  ];
}

const PdpReports = () => {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [anonymise, setAnonymise] = useState(false);
  const [label, setLabel] = useState('Candidate A');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');

  const handleGenerate = (row) => {
    setSelected(row);
    setAnonymise(false);
    setLabel('Candidate A');
    setOpen(true);
  };

  useEffect(() => {
    let mounted = true;
    getCandidates().then((candidates) => {
      if (mounted) setRows(candidates.map((c) => createData(c, handleGenerate)));
    });
    return () => {
      mounted = false;
    };
  }, []);

  const labelOrUndef = () => (anonymise ? label.trim() || 'Candidate A' : undefined);

  const handleDownload = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await generatePdp(selected.id, labelOrUndef());
      setOpen(false);
      setToast('PDP report downloaded.');
    } catch (err) {
      setToast(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadAnswerLog = async (format) => {
    if (!selected) return;
    setBusy(true);
    try {
      await generateAnswerLog(selected.id, labelOrUndef(), format);
      setOpen(false);
      setToast(`Answer log (${format.toUpperCase()}) downloaded.`);
    } catch (err) {
      setToast(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Grid item xs={12}>
      <Typography variant="h5" style={{ marginTop: 16, marginBottom: 8 }}>
        PDP Reports
      </Typography>
      <SortableTable
        name="admin-pdp-reports"
        headers={TABLE_HEADERS}
        rows={rows}
        defaultSortColumnIndex={1}
        searchable
        searchColumnName="name"
      />

      <Dialog open={open} onClose={() => !busy && setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Generate PDP report</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {selected ? `${selected.name} — ${selected.discipline} · ${selected.role}` : ''}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={anonymise}
                color="primary"
                onChange={(e) => setAnonymise(e.target.checked)}
              />
            }
            label="Anonymise for sharing outside Competas"
          />
          <TextField
            fullWidth
            margin="dense"
            label="Anonymised label"
            value={label}
            disabled={!anonymise}
            onChange={(e) => setLabel(e.target.value)}
            helperText="Shown instead of the candidate's name (e.g. Candidate A)."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => handleDownloadAnswerLog('xlsx')} disabled={busy}>
            Answer log (XLSX)
          </Button>
          <Button onClick={() => handleDownloadAnswerLog('csv')} disabled={busy}>
            Answer log (CSV)
          </Button>
          <Button onClick={handleDownload} color="primary" variant="contained" disabled={busy}>
            {busy ? <CircularProgress size={20} /> : 'Download PDF'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast('')}
        message={toast}
      />
    </Grid>
  );
};

export default PdpReports;
